import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import {
  JWT_PROACTIVE_REFRESH_SECONDS,
  decodeJwtExpiry,
  isSessionDenied,
  refreshPlatformToken
} from '@/services/platform-auth';

export type PlatformTokenPair = { accessToken: string; refreshToken: string | null };

export type RefreshReason =
  /** Heartbeat tick — only refresh if the stored access token is close to expiring. */
  | 'expiry'
  /** A request came back 401 — refresh unless someone else already did it for us. */
  | 'unauthorized';

/**
 * Single entry point for renewing the PlatformAuth token pair.
 *
 * PlatformAuth rotates refresh tokens on every use: RefreshTokenCommandHandler revokes the
 * token that was presented and issues a replacement. Presenting an already-consumed token is
 * treated as theft — the handler revokes the entire token family for that user, logging them
 * out of every tab and device at once.
 *
 * Nothing else in the app is serialised with respect to that. Several parallel requests can
 * 401 together when an access token expires, and every open tab runs its own heartbeat over
 * the same shared localStorage. Left alone, the second caller replays a consumed token and
 * takes the whole session down. So every refresh funnels through one critical section:
 *
 *  - `serialize()` orders callers within this tab (and is the whole story on browsers with
 *    no Web Locks support).
 *  - `navigator.locks` extends that ordering across every tab on the origin.
 *  - Inside the section the caller re-reads storage; if another caller already rotated the
 *    pair while it waited, it adopts those tokens instead of spending its own.
 */

const LOCK_NAME = 'sc:platform-token-refresh';

// How long a caller waits for the cross-tab lock before taking it over.
// A healthy holder finishes inside the 20 s refresh timeout, so a longer wait means the browser suspended the holder mid-refresh.
export const LOCK_WAIT_MS = 25_000;

// Pause before the single retry of a refresh that failed on the network.
// The retry lands inside PlatformAuth's 30 s reuse grace, so a rotation the server committed but the browser never received chains onto its replacement.
let retryDelayMs = 1_000;

/** Tail of the in-tab queue. Each entry runs after the previous one settles, pass or fail. */
let tail: Promise<unknown> = Promise.resolve();

function serialize<T>(fn: () => Promise<T>): Promise<T> {
  const run = tail.then(fn, fn);
  // Swallow rejections on the chain itself so one failed refresh doesn't reject every
  // refresh queued behind it — `run` still rejects for the caller that owns it.
  tail = run.catch(() => undefined);
  return run;
}

function withExclusiveAccess<T>(fn: () => Promise<T>): Promise<T> {
  return serialize(() => {
    const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
    return locks?.request ? requestLockWithTimeout(locks, fn) : fn();
  });
}

// A tab the browser suspended in the middle of its refresh keeps the lock until it wakes.
// Waiting on it without limit left woken tabs stuck on 401s with no refresh request ever sent.
// After LOCK_WAIT_MS the caller steals the lock and refreshes anyway.
async function requestLockWithTimeout<T>(locks: any, fn: () => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, LOCK_WAIT_MS);
  // Once the lock is granted the timer must not fire, or a slow refresh would look like a stuck holder.
  const granted = () => {
    clearTimeout(timer);
    return fn();
  };
  try {
    return await locks.request(LOCK_NAME, { signal: controller.signal }, granted);
  } catch (err) {
    if (!timedOut) throw err;
    console.warn(`[AUTH] Token refresh lock not granted within ${LOCK_WAIT_MS} ms, taking it over`);
    return locks.request(LOCK_NAME, { steal: true }, fn);
  }
}

// Retries once when the failure is transient: a dropped connection, a timeout or a 5xx.
// A denial (400, 401, 403 or no refresh token) is final and is rethrown untouched.
async function refreshWithOneRetry(currentRefreshToken: string | null) {
  try {
    return await refreshPlatformToken(currentRefreshToken);
  } catch (err) {
    if (isSessionDenied(err)) throw err;
    console.warn('[AUTH] Token refresh failed, retrying once:', err);
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    // Re-read after the delay — another caller (e.g. a tab that stole the lock) may have
    // successfully rotated the pair while this call was in-flight or suspended. Presenting
    // the captured token again past the server's 30-second grace window revokes the whole
    // session family. If storage moved on, adopt what is there instead of retrying.
    const liveRefreshToken = Storage.getCookie('platformRefreshToken');
    const liveAccessToken = Storage.getCookie('platformAccessToken');
    if (liveRefreshToken && liveRefreshToken !== currentRefreshToken && liveAccessToken) {
      return { accessToken: liveAccessToken, refreshToken: liveRefreshToken };
    }
    return refreshPlatformToken(currentRefreshToken);
  }
}

/**
 * Writes a freshly minted pair to the keys the rest of the app reads: `token` is what
 * Fetch.ts puts in the Authorization header, `platformAccessToken` is the sentinel marking
 * the session as PlatformAuth-backed. They have to move together or the next 401 will look
 * like a legacy session.
 *
 * The duration arguments are inert — storage.ts is localStorage-backed and keeps the
 * parameter only for call-site compatibility — but are passed through unchanged so the
 * values still mean something if it ever goes back to cookies.
 */
function persist(tokens: { accessToken: string; refreshToken: string }) {
  Storage.setCookie('platformAccessToken', tokens.accessToken, 7);
  Storage.setCookie(Enums.Cookie.token, tokens.accessToken, 7);
  if (tokens.refreshToken) {
    Storage.setCookie('platformRefreshToken', tokens.refreshToken, 30);
  }
}

/**
 * Refreshes the PlatformAuth token pair, at most once at a time across every open tab.
 * Persists the result, so callers only need the returned access token to retry with.
 *
 * @param reason               Why the refresh was requested — see `RefreshReason`.
 * @param observedAccessToken  The `platformAccessToken` the caller saw before it hit the
 *                             problem. Lets us recognise a pair that someone else has
 *                             already rotated while this call waited for the lock.
 * @returns The pair now in effect (freshly minted, or adopted from another caller), or
 *          `null` when no refresh was needed or the session has gone away.
 * @throws  `PlatformAuthError` when PlatformAuth refuses to renew the session; see
 *          `isSessionDenied` for which failures mean log out.
 */
export function refreshPlatformSession(
  reason: RefreshReason,
  observedAccessToken: string | null
): Promise<PlatformTokenPair | null> {
  return withExclusiveAccess(async () => {
    const currentAccessToken = Storage.getCookie('platformAccessToken');
    // Logged out (here or in another tab) while we were queued — nothing left to renew.
    if (!currentAccessToken) return null;

    const currentRefreshToken = Storage.getCookie('platformRefreshToken');

    // Storage moved on while we waited, so another caller already exchanged the pair we
    // were holding. Ours is the consumed one now; presenting it would trip PlatformAuth's
    // reuse detection and revoke every session the user has. Adopt theirs instead.
    if (observedAccessToken && currentAccessToken !== observedAccessToken) {
      return { accessToken: currentAccessToken, refreshToken: currentRefreshToken };
    }

    if (reason === 'expiry') {
      const exp = decodeJwtExpiry(currentAccessToken);
      if (!exp) return null;
      if (exp - Math.floor(Date.now() / 1000) > JWT_PROACTIVE_REFRESH_SECONDS) return null;
    }

    const tokens = await refreshWithOneRetry(currentRefreshToken);
    persist(tokens);
    return tokens;
  });
}

/** Test seam — drops the in-tab queue so one test's refresh can't order against another's. */
export function __resetRefreshQueueForTests() {
  tail = Promise.resolve();
}

/** Test seam — shortens the retry pause so the retry path runs in real time. */
export function __setRetryDelayForTests(ms: number) {
  retryDelayMs = ms;
}
